import { isRecord, readArray, readNumber, readString } from "./types";

export type OrderDownloadRuntime = {
  triggerNativeDownload(url: string): void;
  waitForDispatch(): Promise<void>;
};

export type OrderDownloadClaimState = "claimed" | "terminal" | "pending";

export type OrderDownloadClaimPollResult = Readonly<{
  state: OrderDownloadClaimState | "timeout";
  refreshSucceeded: boolean;
}>;

export function mountOrderDownloadFrame(url: string, documentRef: Document = document): HTMLIFrameElement {
  const frame = documentRef.createElement("iframe");
  frame.hidden = true;
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("sandbox", "allow-downloads allow-same-origin");
  frame.referrerPolicy = "same-origin";
  // Attachment navigations do not load a document into the frame. A load event
  // therefore represents the JSON error document and is safe to clean up. Do
  // not use a fixed removal timer: private storage can need longer to verify a
  // large file before the attachment headers are available.
  frame.addEventListener("load", () => frame.remove(), { once: true });
  frame.src = url;
  documentRef.body.append(frame);
  return frame;
}

const DEFAULT_RUNTIME: OrderDownloadRuntime = {
  triggerNativeDownload(url) {
    // The success response is an attachment and opens the browser's native
    // save flow. A JSON/4xx response stays inside this hidden frame and can
    // therefore never replace the customer order page.
    mountOrderDownloadFrame(url);
  },
  waitForDispatch: () => new Promise((resolve) => globalThis.setTimeout(resolve, 750)),
};

/** Observe whether this exact permission was claimed or became unavailable. */
export function orderDownloadClaimState(
  order: unknown,
  permissionId: string,
  previousDownloadCount: number,
): OrderDownloadClaimState {
  const permission = readArray(order, "downloads")
    .find((value) => isRecord(value) && readString(value, "id") === permissionId);
  if (!isRecord(permission)) return "pending";
  const downloadCount = readNumber(permission, "downloadCount") ?? previousDownloadCount;
  if (downloadCount > previousDownloadCount) return "claimed";
  const status = readString(permission, "status");
  return status && status !== "available" ? "terminal" : "pending";
}

/**
 * Polls the private order snapshot until the server has atomically claimed the
 * requested permission. A timeout never means that retrying is safe: callers
 * must keep the button locked and ask the customer to refresh the page first.
 */
export async function pollOrderDownloadClaim(input: Readonly<{
  permissionId: string;
  previousDownloadCount: number;
  refreshOrder(): Promise<unknown>;
  wait(): Promise<void>;
  maximumAttempts?: number;
  cancelled?: () => boolean;
}>): Promise<OrderDownloadClaimPollResult> {
  const maximumAttempts = Math.max(1, Math.trunc(input.maximumAttempts ?? 150));
  let refreshSucceeded = false;
  for (let attempt = 0; attempt < maximumAttempts; attempt += 1) {
    if (input.cancelled?.()) return { state: "timeout", refreshSucceeded };
    try {
      const order = await input.refreshOrder();
      refreshSucceeded = true;
      const state = orderDownloadClaimState(
        order,
        input.permissionId,
        input.previousDownloadCount,
      );
      if (state !== "pending") return { state, refreshSucceeded };
    } catch {
      // A later poll can recover from a transient order-read failure.
    }
    if (attempt + 1 < maximumAttempts) await input.wait();
  }
  return { state: "timeout", refreshSucceeded };
}

/** Accept only this module's exact, same-origin permission URL shape. */
export function safeOrderDownloadUrl(value: string | undefined): string | undefined {
  if (!value || !value.startsWith("/") || value.startsWith("//") || /[\u0000-\u001f\\]/u.test(value)) {
    return undefined;
  }
  try {
    const url = new URL(value, "https://webshops.invalid");
    const keys = [...url.searchParams.keys()];
    const orderIds = url.searchParams.getAll("orderId");
    const permissionIds = url.searchParams.getAll("permission");
    const identifier = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/u;
    if (
      url.origin !== "https://webshops.invalid" ||
      url.pathname !== "/api/modules/webshops/download" ||
      url.hash ||
      keys.length !== 2 ||
      orderIds.length !== 1 ||
      permissionIds.length !== 1 ||
      !identifier.test(orderIds[0] ?? "") ||
      !identifier.test(permissionIds[0] ?? "")
    ) {
      return undefined;
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return undefined;
  }
}

/**
 * Starts a native attachment navigation outside the top-level browsing context
 * and waits briefly before callers refresh the consumed permission state.
 * No response body is ever buffered in JavaScript memory.
 */
export async function issueOrderDownload(
  url: string,
  runtime: OrderDownloadRuntime = DEFAULT_RUNTIME,
): Promise<void> {
  runtime.triggerNativeDownload(url);
  await runtime.waitForDispatch();
}
